import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Skeleton,
    Text,
    VStack,
} from '@chakra-ui/react';
import z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Suspense, useEffect, useState } from 'react';
import { DatePicker } from '@acp/core-ui';
import { AccessIssueProblemSet } from '@acp/shared';
import TinyMCEEditorWithImageUpload from '../tinymce-editor/image-upload';

const MAX_NOTES_LENGTH = 500;

const notifyDiFormSchema = z
    .object({
        date: z.date().optional(),
        notes: z.string().optional(),
    })
    .superRefine((val, ctx) => {
        if (val.notes != null) {
            let trimmed = val.notes
                .replace(/<[^>]*(>|$)|&nbsp;/g, '') // trim nbsp
                .replace(/(<([^>]+)>)/gi, '') // trim html tags
                .replace(/(?:\r\n|\r|\n)/g, ''); // trim line break

            if (trimmed.length < 1) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['notes'],
                    message: 'Notes are required',
                });
            } else if (trimmed.length >= MAX_NOTES_LENGTH) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['notes'],
                    message: `Message should be ${MAX_NOTES_LENGTH} characters or less`,
                });
            }
        }
    });

const TINYMCE_CONTAINER_HEIGHT = 300;

type NotifyDIFormValues = z.infer<typeof notifyDiFormSchema>;

export function NotifyDIForm({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    propertyId,
    problemSet,
    onConfirmed,
    requireNotes = false,
    requireDate = false,
    confirmButtonText,
}: {
    propertyId: number;
    confirmButtonText?: string;
    problemSet: AccessIssueProblemSet;
    requireNotes?: boolean;
    requireDate?: boolean;
    onConfirmed: ({ notes, date }: { notes?: string; date?: Date }) => void;
}) {
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm<NotifyDIFormValues>({
        resolver: zodResolver(notifyDiFormSchema),
        mode: 'onChange',
    });

    useEffect(() => {
        reset({
            date: undefined,
            notes: undefined,
        });
    }, [reset]);

    function onSubmit(formValues: NotifyDIFormValues) {
        onConfirmed({
            notes: formValues.notes,
            date: formValues.date,
        });
    }

    return (
        <VStack align="left">
            <VStack align="left" spacing={6}>
                <HStack>
                    <FormControl>
                        <FormLabel>Issue</FormLabel>
                        <Text>{problemSet.public_name}</Text>
                    </FormControl>

                    {requireDate && (
                        <Controller
                            name="date"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl isInvalid={fieldState.invalid}>
                                    <FormLabel>Resolved date</FormLabel>

                                    <DatePicker
                                        {...field}
                                        onChange={(newDate) => field.onChange(newDate ?? undefined)}
                                        placeholder="Select date"
                                        isInvalid={fieldState.invalid}
                                        dateDisplayFormat="do MMMM yyyy"
                                        size="md"
                                        fromDate={new Date()}
                                    />

                                    {fieldState.error && (
                                        <FormErrorMessage>
                                            {fieldState.error.message}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            )}
                        />
                    )}
                </HStack>

                {requireNotes && (
                    <Controller
                        control={control}
                        name="notes"
                        render={({ field, fieldState }) => (
                            <FormControl isInvalid={fieldState.invalid} isRequired>
                                <FormLabel>Add any notes regarding this issue</FormLabel>
                                <Suspense fallback={<Skeleton height={TINYMCE_CONTAINER_HEIGHT} />}>
                                    <TinyMCEEditorWithImageUpload
                                        containerHeight={TINYMCE_CONTAINER_HEIGHT}
                                        onStartUpload={() => {
                                            setIsUploadingImage(true);
                                        }}
                                        onFinishUpload={() => {
                                            setIsUploadingImage(false);
                                        }}
                                        onEditorChange={field.onChange}
                                        value={field.value}
                                    />
                                </Suspense>
                                {fieldState.error && (
                                    <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                                )}
                            </FormControl>
                        )}
                    />
                )}
            </VStack>
            <HStack justifyContent="end" pt={6}>
                <Button
                    variant="solid"
                    colorScheme="secondary"
                    size="md"
                    onClick={handleSubmit(onSubmit)}
                    isDisabled={isUploadingImage || !isValid}
                >
                    {confirmButtonText ?? 'Confirm'}
                </Button>
            </HStack>
        </VStack>
    );
}
